import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import { cn } from '@/lib/utils'

import {
    Award,
    BadgeCheck,
    BookOpen,
    Briefcase,
    Check,
    ChevronDown,
    CircuitBoard,
    Code,
    Cog,
    FileText,
    Flame,
    QrCode,
    Sigma,
    Ticket,
    TrendingUp
} from 'lucide-react'

const FEATURES = [
    {
        id: 'growth',
        title: "Accelerate 1st-to-4th Year Skill Growth",
        body: "Structured weekly aptitude, coding, CAD and circuit challenges, scored and tracked so every student can see progress semester by semester.",
        icon: TrendingUp
    },
    {
        id: 'vault',
        title: "Centralize Exam PYQs & Lab Manuals",
        body: "Instant access to GKV FET Semester 1–8 study materials curated by the Technical Head, filtered by branch, semester and paper type.",
        icon: BookOpen
    },
    {
        id: 'events',
        title: "Automate Club Events & Verifiable Certificates",
        body: "1-click event registration, QR attendance check-in and printable QR-verified certificates that anyone can confirm in seconds.",
        icon: Award
    },
    {
        id: 'placements',
        title: "Streamline Campus Placements & Hiring Pipeline",
        body: "Kanban hiring stages (Applied → Shortlisted → Interview → Offer → Joined) and top-10% readiness shortlists for recruiters.",
        icon: Briefcase
    }
] as const

type FeatureId = typeof FEATURES[number]['id']

const ease = [0.22, 1, 0.36, 1] as const

const GrowthPreview: FC = (): ReactNode => {
    const challenges = [
        { label: "Aptitude", icon: Sigma, value: 86, color: '#6366F1' },
        { label: "Coding", icon: Code, value: 72, color: '#10B981' },
        { label: "CAD", icon: Cog, value: 64, color: '#F59E0B' },
        { label: "Circuits", icon: CircuitBoard, value: 78, color: '#E11D48' }
    ]

    return (
        <div className='space-y-4'>
            <div className='flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/70'>
                <div>
                    <p className='text-xs font-semibold text-slate-500'>This semester</p>
                    <p className='text-2xl font-extrabold tracking-tight text-ink'>+340 XP</p>
                </div>

                <span className='inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1.5 text-xs font-bold text-orange-600'>
                    <Flame className='size-3.5' />
                    9-week streak
                </span>
            </div>

            <div className='grid grid-cols-2 gap-3'>
                {challenges.map((item, index) => {
                    const circumference = 2 * Math.PI * 26

                    return (
                        <motion.div
                            key={item.label}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.08, duration: 0.5, ease }}
                            className='flex items-center gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/70'
                        >
                            <div className='relative grid size-16 shrink-0 place-items-center'>
                                <svg viewBox='0 0 64 64' className='absolute inset-0 -rotate-90'>
                                    <circle cx='32' cy='32' r='26' fill='none' stroke='#EEF2FF' strokeWidth='6' />
                                    <motion.circle
                                        cx='32'
                                        cy='32'
                                        r='26'
                                        fill='none'
                                        stroke={item.color}
                                        strokeWidth='6'
                                        strokeLinecap='round'
                                        strokeDasharray={circumference}
                                        initial={{ strokeDashoffset: circumference }}
                                        animate={{ strokeDashoffset: circumference * (1 - item.value / 100) }}
                                        transition={{ duration: 1, delay: 0.2 + index * 0.08, ease }}
                                    />
                                </svg>

                                <item.icon className='size-5' style={{ color: item.color }} />
                            </div>

                            <div>
                                <p className='text-sm font-bold text-ink'>{item.label}</p>
                                <p className='text-xs text-slate-500'>{item.value}% of weekly goals</p>
                            </div>
                        </motion.div>
                    )
                })}
            </div>
        </div>
    )
}

const VaultPreview: FC = (): ReactNode => {
    const docs = [
        { title: "Data Structures", meta: "PYQ · End Sem 2024", tone: 'bg-indigo-50 text-indigo-700', tag: 'PYQ' },
        { title: "OOP with C++", meta: "Lab Manual · 14 experiments", tone: 'bg-emerald-50 text-emerald-700', tag: 'Lab' },
        { title: "Discrete Mathematics", meta: "Sessional II · 2024", tone: 'bg-sky-50 text-sky-700', tag: 'Sessional' },
        { title: "DSA Interview Sheet", meta: "150 problems", tone: 'bg-amber-50 text-amber-700', tag: 'Placement' }
    ]

    return (
        <div className='space-y-3'>
            <div className='flex gap-1.5 overflow-hidden'>
                {['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5'].map(sem => (
                    <span key={sem} className={cn('rounded-full px-3 py-1.5 text-xs font-bold', sem === 'Sem 3' ? 'bg-ink text-white' : 'bg-white text-slate-500 ring-1 ring-slate-200')}>
                        {sem}
                    </span>
                ))}
            </div>

            {docs.map((doc, index) => (
                <motion.div
                    key={doc.title}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08, duration: 0.5, ease }}
                    className='flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-sm ring-1 ring-slate-200/70'
                >
                    <span className='grid size-11 shrink-0 place-items-center rounded-xl bg-rose-50 text-rose-600'>
                        <FileText className='size-5' />
                    </span>

                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-bold text-ink'>{doc.title}</p>
                        <p className='text-xs text-slate-500'>{doc.meta}</p>
                    </div>

                    <span className={cn('rounded-md px-2 py-0.5 text-[11px] font-bold', doc.tone)}>{doc.tag}</span>
                </motion.div>
            ))}

            <p className='flex items-center gap-2 pt-1 text-xs font-medium text-slate-500'>
                <BadgeCheck className='size-4 text-emerald-500' />
                Curated by the Technical Head · matched to the GKV FET syllabus
            </p>
        </div>
    )
}

const EventsPreview: FC = (): ReactNode => (
    <div className='space-y-4'>
        <div className='grid grid-cols-3 gap-2'>
            {[
                { label: "Register", icon: Ticket },
                { label: "QR check-in", icon: QrCode },
                { label: "Certificate", icon: Award }
            ].map((step, index) => (
                <motion.div
                    key={step.label}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.45, ease }}
                    className='relative flex flex-col items-center gap-2 rounded-2xl bg-white px-2 py-4 text-center shadow-sm ring-1 ring-slate-200/70'
                >
                    <span className='grid size-10 place-items-center rounded-xl bg-indigo-600 text-white'>
                        <step.icon className='size-5' />
                    </span>

                    <span className='text-xs font-bold text-ink'>{step.label}</span>
                    <span className='absolute right-2 top-2 grid size-4 place-items-center rounded-full bg-emerald-500 text-white'>
                        <Check className='size-2.5' strokeWidth={4} />
                    </span>
                </motion.div>
            ))}
        </div>

        <motion.div
            initial={{ opacity: 0, y: 18, rotate: -1.5 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease }}
            className='relative overflow-hidden rounded-2xl border-2 border-amber-200 bg-[linear-gradient(135deg,#FFFBEB,#FFFFFF_50%,#FEF3C7)] p-5 shadow-[0_20px_40px_-24px_rgba(180,83,9,0.45)]'
        >
            <div className='flex items-start justify-between gap-4'>
                <div>
                    <p className='text-[10px] font-extrabold uppercase tracking-[0.2em] text-amber-700'>Certificate of participation</p>
                    <p className='mt-2 font-serif text-3xl italic leading-none text-ink'>Aarav Sharma</p>
                    <p className='mt-2 text-xs leading-relaxed text-slate-600'>Circuit Troubleshooting Workshop · Tech Club · GKV FET</p>

                    <span className='mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200'>
                        <BadgeCheck className='size-3.5' />
                        QR-verified
                    </span>
                </div>

                <div className='shrink-0 rounded-xl bg-white p-2 ring-1 ring-amber-200'>
                    <QRCodeSVG value='GKV-CERT-CIRCUIT-2026-0042' size={76} level='M' marginSize={0} />
                </div>
            </div>
        </motion.div>
    </div>
)

const PlacementsPreview: FC = (): ReactNode => {
    const stages = [
        { label: "Applied", count: 26, color: '#94A3B8' },
        { label: "Shortlisted", count: 14, color: '#3B82F6' },
        { label: "Interview", count: 8, color: '#8B5CF6' },
        { label: "Offer", count: 5, color: '#F59E0B' },
        { label: "Joined", count: 3, color: '#10B981' }
    ]

    return (
        <div className='space-y-4'>
            <div className='rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/70'>
                <p className='text-xs font-semibold text-slate-500'>Hiring pipeline · Vertex Robotics</p>

                <div className='mt-4 flex items-end gap-2'>
                    {stages.map((stage, index) => (
                        <div key={stage.label} className='flex flex-1 flex-col items-center gap-2'>
                            <span className='text-sm font-extrabold text-ink'>{stage.count}</span>

                            <motion.span
                                initial={{ height: 0 }}
                                animate={{ height: `${Math.max(14, stage.count * 4)}px` }}
                                transition={{ delay: index * 0.08, duration: 0.6, ease }}
                                className='w-full rounded-lg'
                                style={{ backgroundColor: stage.color }}
                            />

                            <span className='text-[10px] font-semibold text-slate-500'>{stage.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className='rounded-2xl bg-ink p-4 text-white'>
                <div className='flex items-center justify-between'>
                    <p className='text-sm font-bold'>Top 10% readiness shortlist</p>
                    <span className='rounded-full bg-emerald-400/15 px-2.5 py-1 text-[11px] font-bold text-emerald-300'>Shared with recruiter</span>
                </div>

                <div className='mt-3 space-y-2'>
                    {[["Sneha Kandpal", "CSE", 93], ["Ananya Bisht", "CSE", 91], ["Arjun Chauhan", "EE", 88]].map(([name, branch, score], index) => (
                        <motion.div
                            key={String(name)}
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 + index * 0.08 }}
                            className='flex items-center gap-3 rounded-xl bg-white/[0.06] px-3 py-2'
                        >
                            <span className='grid size-7 place-items-center rounded-full bg-white/10 text-[10px] font-bold'>{index + 1}</span>
                            <span className='flex-1 text-sm font-medium'>{name}</span>
                            <span className='text-xs text-slate-400'>{branch}</span>
                            <span className='font-mono text-sm font-bold text-emerald-300'>{score}</span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    )
}

const PREVIEWS: Record<FeatureId, FC> = {
    growth: GrowthPreview,
    vault: VaultPreview,
    events: EventsPreview,
    placements: PlacementsPreview
}

export const FeatureAccordion: FC = (): ReactNode => {
    const [active, setActive] = useState<FeatureId>('growth')
    const [autoPlay, setAutoPlay] = useState(true)
    const [hovered, setHovered] = useState(false)
    const sectionRef = useRef<HTMLElement>(null)
    const inView = useInView(sectionRef, { margin: '-25% 0px -25% 0px' })
    const reduceMotion = useReducedMotion()
    const Preview = PREVIEWS[active]

    const advance = () => {
        const index = FEATURES.findIndex(feature => feature.id === active)

        setActive(FEATURES[(index + 1) % FEATURES.length].id)
    }

    return (
        <section ref={sectionRef} id='features' aria-labelledby='features-title' className='bg-white px-5 py-24 sm:px-6 sm:py-28'>
            <div className='mx-auto grid max-w-7xl gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-16'>
                <div>
                    <p className='text-[11px] font-bold uppercase tracking-[0.18em] text-crimson'>Platform capabilities</p>

                    <h2 id='features-title' className='mt-4 text-balance text-4xl font-extrabold leading-[1.1] tracking-tight text-ink sm:text-5xl'>
                        What GKV Tech Campus can do <span className='font-serif font-normal italic text-indigo-600'>for you</span>
                    </h2>

                    <div className='mt-10 border-t border-slate-200' onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                        {FEATURES.map(feature => {
                            const selected = feature.id === active

                            return (
                                <div key={feature.id} className='relative border-b border-slate-200'>
                                    <h3>
                                        <button
                                            type='button'
                                            id={`feature-${feature.id}`}
                                            aria-expanded={selected}
                                            aria-controls={`feature-panel-${feature.id}`}
                                            onClick={() => {
                                                setActive(feature.id)
                                                setAutoPlay(false)
                                            }}
                                            className='group flex w-full items-center gap-4 py-6 text-left'
                                        >
                                            <span className={cn('grid size-11 shrink-0 place-items-center rounded-xl transition-colors duration-300', selected ? 'bg-ink text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200')}>
                                                <feature.icon className='size-5' />
                                            </span>

                                            <span className={cn('flex-1 text-lg font-bold tracking-tight transition-colors sm:text-xl', selected ? 'text-ink' : 'text-slate-500 group-hover:text-ink')}>
                                                {feature.title}
                                            </span>

                                            <ChevronDown className={cn('size-5 shrink-0 text-slate-400 transition-transform duration-300', selected && 'rotate-180 text-ink')} />
                                        </button>
                                    </h3>

                                    <AnimatePresence initial={false}>
                                        {selected && (
                                            <motion.div
                                                id={`feature-panel-${feature.id}`}
                                                role='region'
                                                aria-labelledby={`feature-${feature.id}`}
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.4, ease }}
                                                className='overflow-hidden'
                                            >
                                                <p className='pb-6 pl-[60px] pr-4 text-[15px] leading-relaxed text-slate-600'>
                                                    {feature.body}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {selected && autoPlay && !reduceMotion && (
                                        <span
                                            key={`${feature.id}-progress`}
                                            aria-hidden='true'
                                            onAnimationEnd={advance}
                                            style={{ animationPlayState: hovered || !inView ? 'paused' : 'running' }}
                                            className='absolute -bottom-px left-0 h-0.5 w-full origin-left animate-progress-x bg-crimson'
                                        />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className='lg:sticky lg:top-28 lg:self-start'>
                    <div className='relative overflow-hidden rounded-3xl bg-periwinkle p-6 shadow-sm ring-1 ring-indigo-100 sm:p-8'>
                        <div aria-hidden='true' className='pointer-events-none absolute -right-20 -top-20 size-64 rounded-full bg-indigo-200/50 blur-3xl' />
                        <div aria-hidden='true' className='pointer-events-none absolute -bottom-24 -left-16 size-64 rounded-full bg-rose-200/40 blur-3xl' />

                        <div className='relative min-h-[380px]'>
                            <AnimatePresence mode='wait' initial={false}>
                                <motion.div
                                    key={active}
                                    initial={{ opacity: 0, y: 16, filter: 'blur(6px)' }}
                                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                    exit={{ opacity: 0, y: -12, filter: 'blur(6px)' }}
                                    transition={{ duration: 0.4, ease }}
                                >
                                    <Preview />
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
